import { creditSourceTypeSchema, type PaymentMethod, paymentMethodSchema } from '@sahab/contracts'
import { z } from 'zod'
import { isIsoDate } from '../../lib/dates'
import { PAGE_SIZES } from '../sales/search'

/**
 * The refunds list lives in the URL (shareable, back-button safe), like the other sales lists: every
 * key is optional and an invalid value falls back to the default instead of breaking the page.
 */
type PageSize = (typeof PAGE_SIZES)[number]

const isoDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)
const uuid = z.uuid().optional().catch(undefined)

const REFUND_SORT_FIELDS = ['refundDate', 'number', 'amount', 'customer'] as const
export type RefundSort = (typeof REFUND_SORT_FIELDS)[number] | `-${(typeof REFUND_SORT_FIELDS)[number]}`

export function isRefundSort(value: string): value is RefundSort {
  return (REFUND_SORT_FIELDS as readonly string[]).includes(value.startsWith('-') ? value.slice(1) : value)
}

export const REFUND_DEFAULT_SORT = '-refundDate'
/** The list's view tabs after All (the contract's REFUND_VIEWS). */
export const REFUND_VIEWS = ['void'] as const
export type RefundView = (typeof REFUND_VIEWS)[number]

export const refundSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z
    .number()
    .refine((n): n is PageSize => (PAGE_SIZES as readonly number[]).includes(n))
    .optional()
    .catch(undefined),
  sort: z.string().refine(isRefundSort).optional().catch(undefined),
  q: z.string().trim().max(200).optional().catch(undefined),
  view: z.enum(REFUND_VIEWS).optional().catch(undefined),
  method: paymentMethodSchema.optional().catch(undefined),
  /** The cash or bank account paid from. */
  account: uuid,
  customerId: uuid,
  from: isoDate,
  to: isoDate,
})
export type RefundSearch = z.infer<typeof refundSearchSchema>

export interface RefundListParams {
  page: number
  pageSize: number
  sort: string
  q?: string | undefined
  status?: 'POSTED' | 'VOID' | undefined
  method?: PaymentMethod | undefined
  treasuryAccountId?: string | undefined
  customerId?: string | undefined
  from?: string | undefined
  to?: string | undefined
}

export type RefundSummaryParams = Omit<RefundListParams, 'page' | 'pageSize' | 'sort' | 'status'>

export function toRefundListQuery(search: RefundSearch): RefundListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? REFUND_DEFAULT_SORT,
    q: search.q || undefined,
    status: search.view === 'void' ? 'VOID' : undefined,
    method: search.method,
    treasuryAccountId: search.account,
    customerId: search.customerId,
    from: search.from,
    to: search.to,
  }
}

export function toRefundSummaryQuery(query: RefundListParams): RefundSummaryParams {
  return {
    q: query.q,
    method: query.method,
    treasuryAccountId: query.treasuryAccountId,
    customerId: query.customerId,
    from: query.from,
    to: query.to,
  }
}

/**
 * Refund credit: `?customerId=` preselects the customer; `?sourceType=&sourceId=` (from a receipt or
 * credit note page) fills in that credit's remainder.
 */
export const newRefundSearchSchema = z.object({
  customerId: uuid,
  sourceType: creditSourceTypeSchema.optional().catch(undefined),
  sourceId: uuid,
})
